import { MapBounds } from '@/pages/AuctionsNearMe/components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
    defaultHitsPerPage,
    defaultPageNumber,
} from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { createBoundingBoxString } from '@/pages/AuctionsNearMe/helpers/createBoundingBoxString';
import { findGeoAreaFromUrlParams, getStateCodeFromName } from '@/pages/AuctionsNearMe/helpers/geoAreaToUrlParams';
import {
    AuctionsNearMeQueryParams,
    AuctionsNearMeRouteParams,
} from '@/pages/AuctionsNearMe/types/AuctionsNearMe.types';
import {
    fetchCatalogsNearMeByBounds,
    fetchCatalogsNearMeByCityAndState,
} from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { markLocationInvalid } from '@/redux/modules/auctionsNearMe/auctionsNearMe.reducer';
import { getCatalogsNearMeSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { loadAllGeoAreas } from '@/redux/modules/geoArea/geoArea';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { parseQueryParams } from '@/utils/queryParams';

const auctionsNearMePageData = async ({ location, match, store }: DataLoaderParams<AuctionsNearMeRouteParams>) => {
    const { dispatch, getState } = store;

    // Parse query parameters to check for custom search
    const queryParams = parseQueryParams<AuctionsNearMeQueryParams>(location.search);

    // If there's a search query, skip fetching - the component will handle it via geocoding
    if (queryParams.search) {
        // Still load geoAreas and fetch ad, but don't fetch catalogs
        await Promise.all([
            dispatch(loadAllGeoAreas()),
            dispatch(fetchCatalogAdIfNeeded({ pageName: 'leaderboard-auctions-near-me' })),
        ]);
        return;
    }

    const { city, country, state } = match.params;
    const adFetchPromise = dispatch(fetchCatalogAdIfNeeded({ pageName: 'leaderboard-auctions-near-me' }));

    // Hydrates the SSR markup with the full catalog records behind the results.
    const fetchCatalogDetails = async () => {
        const catalogIds = getCatalogsNearMeSelector(getState()).map((catalog) => catalog.id);

        if (catalogIds.length > 0) {
            await dispatch(fetchCatalogsIfNeeded({ catalogIds }));
        }
    };

    // City + state URLs (e.g. /find-auctions-near-me/fl/st-petersburg):
    // Use the city/state endpoint so the backend resolves the location.
    // Avoids relying on the client-side Google Maps geocoder which leaves SSR
    // showing the default USA bounds count (~1000+) instead of the real count.
    if (city && state) {
        // near-me-v2 filters on the 2-letter code, so a full state name ("ohio") returns zero
        // results rather than an error. Normalize first, and reject what does not resolve at all
        // (or just repeats the city segment) before spending a backend call on it.
        const stateCode = getStateCodeFromName(state);
        const citySegment = city.toLowerCase();
        const isMalformedLocation = !stateCode || stateCode === citySegment || state.toLowerCase() === citySegment;

        if (isMalformedLocation) {
            dispatch(markLocationInvalid());
            await adFetchPromise;
            return;
        }

        await Promise.all([
            dispatch(loadAllGeoAreas()),
            dispatch(
                fetchCatalogsNearMeByCityAndState({
                    city,
                    page: defaultPageNumber,
                    pageSize: defaultHitsPerPage,
                    state: stateCode,
                })
            ),
            adFetchPromise,
        ]);

        await fetchCatalogDetails();
        return;
    }

    await dispatch(loadAllGeoAreas());

    // Find geoArea from URL params (state-only or country-only)
    const allGeoAreas = Object.values(getState().geoArea.byId);
    const geoArea = findGeoAreaFromUrlParams(allGeoAreas, {
        country,
        state,
    });

    // Single-segment URLs (/find-auctions-near-me/colorado/, /wyoming/, /tronto/) carry a location
    // we have to resolve ourselves. Only the bare /find-auctions-near-me/ landing page is allowed
    // to fall back to the default country bounds — doing so for an unresolved segment is what
    // served the whole ~1,200-auction catalog under URLs like /normal-business/.
    // `city` is absent by construction: it only exists on the /:state/:city/ route, which the
    // branch above already resolved and returned from.
    const hasLocationSegment = Boolean(country || state);

    if (hasLocationSegment && !geoArea) {
        // Only 24 US states have a geoArea record, so a miss does not mean the place is fake.
        // A real state or province still has auctions worth showing; query it by code instead.
        // `:state` and `:country` are same-rank routes matched in declaration order, so the
        // segment lands in `state` today — read both so a route reorder cannot silently 404 it.
        const stateCode = getStateCodeFromName(state || country || '');

        if (stateCode) {
            await Promise.all([
                dispatch(
                    fetchCatalogsNearMeByCityAndState({
                        page: defaultPageNumber,
                        pageSize: defaultHitsPerPage,
                        state: stateCode,
                    })
                ),
                adFetchPromise,
            ]);

            await fetchCatalogDetails();
            return;
        }

        // An empty geoArea list means the geographic API failed, not that the URL is bad —
        // keep the old default-bounds behavior rather than 404ing every location page.
        if (allGeoAreas.length > 0) {
            dispatch(markLocationInvalid());
            await adFetchPromise;
            return;
        }
    }

    let mapBounds: MapBounds =
        getState().intl.locale.toLowerCase() === 'en-gb'
            ? defaultAuctionsNearMeUkBounds
            : defaultAuctionsNearMeUsaBounds;

    if (geoArea) {
        mapBounds = {
            northEast: {
                lat: geoArea.northEastLatitude,
                lng: geoArea.northEastLongitude,
            },
            southWest: {
                lat: geoArea.southWestLatitude,
                lng: geoArea.southWestLongitude,
            },
        };
    }

    // Fetch catalogs within the map bounds
    const insideBoundingBox = createBoundingBoxString(mapBounds);

    await Promise.all([
        dispatch(
            fetchCatalogsNearMeByBounds({
                insideBoundingBox,
                page: defaultPageNumber,
                pageSize: defaultHitsPerPage,
            })
        ),
        adFetchPromise,
    ]);

    await fetchCatalogDetails();
};

export default auctionsNearMePageData;
